import { useCallback, useEffect, useRef, useState } from "react";

import {
  isUpstreamTimeoutResponse,
  isValidationError,
} from "~/components/forms/validationErrorResponse";
import { claimUncertainAdd } from "~/hec/basket/uncertain-add-claims";

/**
 * useBasketAddReconcile — bounded "uncertain add" protocol for a basket add
 * whose POST outlived the client deadline (hotfix v2026.15.02 review, P1
 * round 2).
 *
 * WHY A PROTOCOL, NOT A TIMER. `add-to-cart.tsx` deadlines the tile add at
 * 10s (`useFetcherDeadline`). Round 1 of this hotfix then reset the fetcher,
 * re-enabled the button and scheduled ONE basket reload 10s later, reasoning
 * that "past the SCAPI budget (8s) the write has either landed or never will".
 * That assumption is wrong: 8s is the budget of ONE upstream call
 * (`UPSTREAM_TIMEOUTS_MS.scapi`), not of the whole action. The add action
 * (`routes/resources/basket.tsx` → `shop.addItemToBasket`) is a chain of
 * sequential upstream steps, and — since the same hotfix stopped the Worker's
 * wall-clock cap from racing POSTs (`isCappableRequest`) — nothing bounds the
 * request as a whole. A shopper who saw an apparently unchanged cart, added
 * again, and then had the original write arrive is exactly the double add the
 * copy change was meant to prevent.
 *
 * BOUNDED ACTION LIFETIME `L` — the worst-case SEQUENTIAL chain from the
 * request reaching the Worker to the add write LANDING at SCAPI. Every SCAPI
 * call is capped client-side at 8s (`upstreamSignal("scapi")`, minted per
 * call), SLAS at 5s, a KV session commit at ≈1s (2 retries × 200/400ms backoff
 * + the put, `session-commit.ts`). Steps that run in PARALLEL with the add
 * (`shop.getProducts` for the modal payload) and steps that run AFTER the
 * write (shipping-method bind, bundle enrichment, cart-update analytics) are
 * excluded — they delay the RESPONSE, not the write.
 *
 *   1. SLAS token for the request (`requireCustomerId` → `auth.ready`)     5s
 *   2. no pinned basket → `getBasket("resolve-or-create")`:
 *        getCustomerBaskets 8s → createBasket 8s → recovery
 *        getCustomerBaskets 8s → commitSession ≈1s                        25s
 *   3. `addItemToBasket` re-commits the pin                                1s
 *   4. first add against a stale pin (BasketNotFound is a fast 404, but
 *      the BOUND is the budget)                                            8s
 *   5. stale-pin recovery: createBasket 8s → getCustomerBaskets 8s →
 *      commitSession ≈1s                                                  17s
 *   6. the retried add — the write. The client budget is 8s, but SCAPI's
 *      own hook-inclusive server timeout is 10s, so a write the client
 *      abandoned at 8s can still commit up to 10s after it was issued     10s
 *                                                                   L = 66s
 *
 * WINDOW AND CEILING. The protocol polls the basket while the write can still
 * land, plus enough tail to observe it: L (66s) + the last poll interval
 * (≤16s) + reload latency (~5s), rounded → 90s after submit, i.e.
 * `BASKET_ADD_UNCERTAIN_WINDOW_MS` (80s) after the 10s deadline. 90s is ALSO
 * the hard ceiling: POSTs are uncapped, so a parked invocation (COM-67 class)
 * can in principle resume minutes later, but holding a tile in "Confirming…"
 * for minutes is a worse outcome than telling the shopper to check the cart.
 * Past the ceiling only that unbounded class remains — which is why the
 * DURABLE fix is an idempotency key on the add action (client-generated key
 * echoed by the action, de-duplicated server-side). That is out of hotfix
 * scope; this protocol is the hotfix-sized stand-in.
 *
 * THE PROTOCOL.
 *   - `arm(productId, quantity)` right before `fetcher.submit`: snapshots the
 *     product's CURRENT basket quantity (the baseline) through the provider's
 *     ref-backed reader — the tile never subscribes to the basket.
 *   - `enter()` from the deadline handler (phase "submit"): the tile is now
 *     UNCERTAIN — button disabled with confirming copy, a second add refused.
 *     The fetcher is deliberately KEPT ALIVE (no `reset()`): a reset aborts
 *     only the browser side while the Worker keeps working, and the in-flight
 *     response is the most direct confirmation available. A late
 *     `successfullyAdded` resolves the protocol and lets the normal
 *     added-to-basket modal open; a late validation error resolves it as
 *     rejected (the flyout watcher toasts the server message).
 *   - Polling on `BASKET_ADD_RECONCILE_SCHEDULE_MS` (offsets after the
 *     deadline). Each tick READS the product's quantity first — so it observes
 *     the previous tick's reload, or any other reload that landed meanwhile —
 *     and resolves `confirmed-late` when `quantity − baseline ≥ requested`;
 *     otherwise it issues one `reconcileBasket()` reload (the shared keyed
 *     "basket" loader — a GET, so it IS wall-clock capped) and schedules the
 *     next tick. The final tick only reads and resolves `unconfirmed`.
 *   - Terminal states: `confirmed-late` (source `poll` | `response`),
 *     `rejected-late` (`response`), `unconfirmed` (`window`), `abandoned`
 *     (`unmount` — the tile left the page mid-window; timers stop, the fetcher
 *     is left to React Router so `BasketProvider` still refreshes the header
 *     count when the response settles). Every terminal state reaches
 *     `onOutcome` with `elapsedMs` (since submit) and `polls`, so the
 *     production rate of late-landing writes is measurable.
 *   - `reset()` is called only when the protocol ends with the POST still
 *     open on the client (poll-confirmed / window elapsed): it returns the
 *     tile to idle and lets `BasketProvider`'s mutation-settled reload fire.
 *
 * Instances are independent: two tiles uncertain at once each run their own
 * timers and resolve on their own product's quantity.
 */

/** Worst-case sequential lifetime of the add action up to the write landing —
 *  the derivation is in the module header. Documentation + test invariant. */
export const BASKET_ADD_ACTION_LIFETIME_MS = 66_000;

/** How long after the 10s deadline the tile stays uncertain (→ 90s after
 *  submit). Also the ceiling — see WINDOW AND CEILING above. */
export const BASKET_ADD_UNCERTAIN_WINDOW_MS = 80_000;

/**
 * Poll ticks, as offsets after the deadline. Every tick reads; every tick but
 * the last then reloads. Backoff (~1.4x) that flattens toward the end so the
 * window does not close on one long gap; 7 reloads over 80s for a path that
 * production measures as rare (0–3 uncapped-POST stalls per 3.7h).
 */
export const BASKET_ADD_RECONCILE_SCHEDULE_MS: readonly number[] = [
  0,
  5_000,
  12_000,
  22_000,
  36_000,
  52_000,
  68_000,
  BASKET_ADD_UNCERTAIN_WINDOW_MS,
];

export type BasketAddOutcome =
  | "confirmed-late"
  | "rejected-late"
  | "unconfirmed"
  | "abandoned";

export type BasketAddOutcomeSource = "response" | "poll" | "window" | "unmount";

export interface BasketAddOutcomeContext {
  productId: string;
  /** Quantity the shopper asked for. */
  quantity: number;
  /** ms since the add was submitted. */
  elapsedMs: number;
  /** Basket reloads this protocol instance issued. */
  polls: number;
  source: BasketAddOutcomeSource;
}

/**
 * Reads a product's CURRENT top-level basket quantity (lines with that
 * productId, summed). `undefined` = no basket loaded (unknown), distinct from
 * `0` = loaded and the product is not in it. Published by `BasketProvider`
 * on a stable context (`useBasketLineQuantityReader`).
 */
export type BasketLineQuantityReader = (
  productId: string,
) => number | undefined;

type FetcherState = "idle" | "loading" | "submitting";

/** The slice of a React Router fetcher this protocol needs. */
export interface ReconcilableFetcher {
  state: FetcherState;
  data: unknown;
  reset: () => void;
}

interface ArmedAdd {
  productId: string;
  quantity: number;
  /** Product quantity in the basket at submit; `undefined` = unknown, which
   *  disables quantity-based confirmation (the response can still confirm). */
  baseline: number | undefined;
  submittedAt: number;
}

interface ActiveProtocol extends ArmedAdd {
  polls: number;
}

const isSuccessfulAdd = (data: unknown): boolean =>
  !!data &&
  typeof data === "object" &&
  "successfullyAdded" in data &&
  (data as { successfullyAdded?: unknown }).successfullyAdded === true;

export function useBasketAddReconcile({
  fetcher,
  reconcileBasket,
  readLineQuantity,
  onOutcome,
  onEnterFromResponse,
}: {
  fetcher: ReconcilableFetcher;
  reconcileBasket: () => void;
  readLineQuantity: BasketLineQuantityReader;
  onOutcome: (
    outcome: BasketAddOutcome,
    context: BasketAddOutcomeContext,
  ) => void;
  /** Called when the protocol starts because the add ANSWERED with a COM-804
   *  503 `upstreamTimeout` before the client deadline (not from `enter`). */
  onEnterFromResponse?: (context: {
    productId: string;
    quantity: number;
  }) => void;
}): {
  /** True while the add is being confirmed — disable the control. */
  uncertain: boolean;
  /** Call right before submitting the add. */
  arm: (productId: string, quantity: number) => void;
  /** Call from the deadline handler (phase "submit"). Returns false when a
   *  protocol is already running for this tile. */
  enter: (fallback: {
    productId: string;
    quantity: number;
    deadlineMs: number;
  }) => boolean;
} {
  const [uncertain, setUncertain] = useState(false);
  const armedRef = useRef<ArmedAdd | null>(null);
  const activeRef = useRef<ActiveProtocol | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  // Live claim on the product's uncertain-add failure UX (see
  // `uncertain-add-claims.ts`): held from `arm` to any terminal path/unmount.
  const releaseClaimRef = useRef<(() => void) | null>(null);
  const releaseClaim = useCallback(() => {
    releaseClaimRef.current?.();
    releaseClaimRef.current = null;
  }, []);

  // Latest-value refs: timer callbacks and the unmount cleanup must see the
  // current fetcher / callbacks, never the render that armed them.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const reconcileRef = useRef(reconcileBasket);
  reconcileRef.current = reconcileBasket;
  const readRef = useRef(readLineQuantity);
  readRef.current = readLineQuantity;
  const onOutcomeRef = useRef(onOutcome);
  onOutcomeRef.current = onOutcome;
  const onEnterFromResponseRef = useRef(onEnterFromResponse);
  onEnterFromResponseRef.current = onEnterFromResponse;

  const finish = useCallback(
    (outcome: BasketAddOutcome, source: BasketAddOutcomeSource) => {
      const active = activeRef.current;
      if (!active) return;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      activeRef.current = null;
      armedRef.current = null;
      releaseClaim();
      if (mountedRef.current) setUncertain(false);
      // Only a protocol that ends with the POST still open on the client
      // resets it. A `response` outcome means the fetcher is settling on its
      // own (and the flyout watcher must still see its data); on unmount the
      // fetcher is left to React Router.
      if (
        (source === "poll" || source === "window") &&
        fetcherRef.current.state !== "idle"
      ) {
        fetcherRef.current.reset();
      }
      onOutcomeRef.current(outcome, {
        productId: active.productId,
        quantity: active.quantity,
        elapsedMs: Date.now() - active.submittedAt,
        polls: active.polls,
        source,
      });
    },
    [releaseClaim],
  );

  // Self-scheduling tick, reached through a ref so the recursion never captures
  // a stale closure.
  const tickRef = useRef<(index: number) => void>(() => {});
  const tick = useCallback(
    (index: number) => {
      const active = activeRef.current;
      if (!active) return;
      timerRef.current = null;
      const current = readRef.current(active.productId);
      const landed =
        active.baseline !== undefined &&
        current !== undefined &&
        current - active.baseline >= active.quantity;
      if (landed) {
        finish("confirmed-late", "poll");
        return;
      }
      const at = BASKET_ADD_RECONCILE_SCHEDULE_MS[index] ?? 0;
      const next = BASKET_ADD_RECONCILE_SCHEDULE_MS[index + 1];
      if (next === undefined) {
        finish("unconfirmed", "window");
        return;
      }
      active.polls += 1;
      reconcileRef.current();
      timerRef.current = setTimeout(
        () => tickRef.current(index + 1),
        next - at,
      );
    },
    [finish],
  );
  tickRef.current = tick;

  const arm = useCallback(
    (productId: string, quantity: number) => {
      releaseClaim();
      releaseClaimRef.current = claimUncertainAdd(productId);
      armedRef.current = {
        productId,
        quantity,
        baseline: readRef.current(productId),
        submittedAt: Date.now(),
      };
    },
    [releaseClaim],
  );

  const enter = useCallback(
    (fallback: { productId: string; quantity: number; deadlineMs: number }) => {
      if (activeRef.current) return false;
      // Un-armed entry (a submission this hook did not see start): no
      // baseline, so only the response can confirm; the polls still refresh
      // the header count and the window still bounds the uncertain state.
      const armed = armedRef.current ?? {
        productId: fallback.productId,
        quantity: fallback.quantity,
        baseline: undefined,
        submittedAt: Date.now() - fallback.deadlineMs,
      };
      armedRef.current = null;
      activeRef.current = { ...armed, polls: 0 };
      setUncertain(true);
      // Deferred (not synchronous) so the caller can show its confirming
      // state before an immediate confirmation could replace it.
      timerRef.current = setTimeout(
        () => tickRef.current(0),
        BASKET_ADD_RECONCILE_SCHEDULE_MS[0] ?? 0,
      );
      return true;
    },
    [],
  );

  // The response channel: while uncertain, the fetcher leaving `submitting`
  // means the action answered. `loading` already carries the action result
  // (RR7 publishes it before the revalidation), so no need to wait for idle.
  useEffect(() => {
    if (!uncertain || !activeRef.current) return;
    if (fetcher.state === "submitting") return;
    if (isSuccessfulAdd(fetcher.data)) {
      finish("confirmed-late", "response");
    } else if (
      isValidationError(fetcher.data) &&
      !isUpstreamTimeoutResponse(fetcher.data)
    ) {
      finish("rejected-late", "response");
    }
    // Settled with nothing definitive (the action threw to the error
    // boundary, or answered COM-804's 503 `upstreamTimeout` — a timed-out
    // write may still have landed): keep polling.
  }, [uncertain, fetcher.state, fetcher.data, finish]);

  // COM-804 early-answer entry. SCAPI calls now time out at ~8s, so a hung add
  // answers with a 503 `upstreamTimeout` BEFORE the 10s client deadline and the
  // deadline path (`enter`) never runs — yet the write may still commit (SCAPI's
  // hook-inclusive server timeout is 10s). An ARMED add (this hook saw it start;
  // cleared on every terminal state and on an idle settle, so stale fetcher data
  // from a finished add cannot re-trigger) that settles with that response
  // therefore enters the protocol immediately: poll the basket, never report a
  // plain failure.
  useEffect(() => {
    if (activeRef.current || !armedRef.current) return;
    if (fetcher.state === "submitting") return;
    if (!isUpstreamTimeoutResponse(fetcher.data)) return;
    const armed = armedRef.current;
    armedRef.current = null;
    activeRef.current = { ...armed, polls: 0 };
    setUncertain(true);
    timerRef.current = setTimeout(
      () => tickRef.current(0),
      BASKET_ADD_RECONCILE_SCHEDULE_MS[0] ?? 0,
    );
    onEnterFromResponseRef.current?.({
      productId: armed.productId,
      quantity: armed.quantity,
    });
  }, [fetcher.state, fetcher.data]);

  // A submission that settled normally leaves no baseline behind for a later
  // one to misread. (`enter` runs from a timer, never from this effect, so an
  // armed add cannot be cleared between arm and its `submitting` render.)
  useEffect(() => {
    if (fetcher.state === "idle" && !activeRef.current) {
      armedRef.current = null;
      releaseClaim();
    }
  }, [fetcher.state, releaseClaim]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      releaseClaim();
      if (activeRef.current) finish("abandoned", "unmount");
    };
  }, [finish, releaseClaim]);

  return { uncertain, arm, enter };
}
